import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { apiService } from '../../services/api';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Avatar,
  Chip,
  LinearProgress,
  Divider,
  IconButton,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Paper
} from '@mui/material';
import {
  Assignment as ChecklistIcon,
  Receipt as ExpenseIcon,
  Build as MaintenanceIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Add as AddIcon,
  TrendingUp as TrendingIcon,
  Notifications as NotificationIcon,
  FileText as FileTextIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { useAuth } from '../../hooks/useAuth';
import MobileServiceCard from './MobileServiceCard';
import MobileExpenseCard from './MobileExpenseCard';
import '../../styles/mobile-first.css';

const MobileDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState({
    latestChecklist: null,
    historicalChecklists: [],
    upcomingMaintenance: [],
    currentExpenseReport: null,
    bankStatement: null
  });
  const [safetyLetter, setSafetyLetter] = useState(null);
  const [safetyLetterDialogOpen, setSafetyLetterDialogOpen] = useState(false);
  const [safetyLetterSignature, setSafetyLetterSignature] = useState('');
  const [safetyLetterSigning, setSafetyLetterSigning] = useState(false);
  const [safetyLetterError, setSafetyLetterError] = useState('');
  const [safetyLetterViewAttempted, setSafetyLetterViewAttempted] = useState(false);
  const { user } = useAuth();

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      let dashboardResponse = { latestChecklist: null, historicalChecklists: [], upcomingMaintenance: [] };
      let expenseReportResponse = null;
      let bankStatementResponse = null;
      let safetyLetterResponse = null;

      try {
        dashboardResponse = await apiService.getUserDashboard(user.id);
      } catch (error) {
        console.error('Error fetching main dashboard data:', error);
      }

      try {
        const today = new Date();
        const currentMonth = today.getMonth() + 1;
        const currentYear = today.getFullYear();
        
        expenseReportResponse = await apiService.getOrCreateMonthlyReport({
          month: currentMonth,
          year: currentYear
        });
        
        bankStatementResponse = await apiService.getUserBankStatement(currentMonth, currentYear);
      } catch (error) {
        console.error('Error fetching expense report data:', error);
      }

      try {
        safetyLetterResponse = await apiService.getCurrentMonthSafetyLetter();
        setSafetyLetter(safetyLetterResponse);
      } catch (error) {
        console.error('Error fetching safety letter:', error);
      }

      setDashboardData({
        latestChecklist: dashboardResponse.latestChecklist || null,
        historicalChecklists: dashboardResponse.recentChecklists || [],
        upcomingMaintenance: dashboardResponse.upcomingMaintenance || [],
        currentExpenseReport: expenseReportResponse?.report || null,
        bankStatement: bankStatementResponse || null
      });
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to fetch dashboard data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleSafetyLetterView = () => {
    setSafetyLetterViewAttempted(true);
    setSafetyLetterError('');
    // Open PDF in new window
    if (safetyLetter?.file_url) {
      window.open(safetyLetter.file_url, '_blank');
    } else {
      setSafetyLetterError('Unable to open safety letter. Please try again or contact support.');
    }
  };

  const handleSafetyLetterSign = async () => {
    if (!safetyLetterViewAttempted) {
      setSafetyLetterError('Please view the safety letter before signing');
      return;
    }

    if (!safetyLetterSignature.trim()) {
      setSafetyLetterError('Please provide your signature');
      return;
    }

    setSafetyLetterSigning(true);
    try {
      await apiService.signSafetyLetter(safetyLetter.id, safetyLetterSignature);
      setSafetyLetterDialogOpen(false);
      setSafetyLetterSignature('');
      setSafetyLetterError('');
      setSafetyLetterViewAttempted(false);
      // Refresh dashboard to show signed letter
      await fetchDashboardData();
    } catch (err) {
      console.error('Error signing safety letter:', err);
      setSafetyLetterError('Failed to sign safety letter. Please try again.');
    } finally {
      setSafetyLetterSigning(false);
    }
  };

  const formatSafetyLetterDate = (year, month) => {
    return new Date(year, month - 1).toLocaleString('default', { 
      month: 'long',
      year: 'numeric'
    });
  };

  useEffect(() => {
    if (user?.id) {
      fetchDashboardData();
    }
  }, [user]);

  if (loading) {
    return (
      <Box className="mobile-padding-md" sx={{ textAlign: 'center', pt: 4 }}>
        <CircularProgress size={40} />
        <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
          Loading your dashboard...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="mobile-padding-md">
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const getWelcomeMessage = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const calculateCompletionScore = () => {
    const tasks = [
      dashboardData.latestChecklist,
      dashboardData.currentExpenseReport,
      safetyLetter?.has_signed
    ];
    const completed = tasks.filter(Boolean).length;
    return Math.round((completed / tasks.length) * 100);
  };

  return (
    <Box className="mobile-scroll-area" sx={{ height: '100%' }}>
      {/* Header Card */}
      <Card className="mobile-card" sx={{ margin: '16px', marginBottom: '8px' }}>
        <CardContent sx={{ pb: '16px !important' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Avatar
              sx={{
                bgcolor: 'primary.main',
                width: 48,
                height: 48,
                mr: 2,
                fontSize: '20px',
                fontWeight: 'bold'
              }}
            >
              {user?.username?.charAt(0).toUpperCase()}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" className="mobile-title" sx={{ mb: 0.5 }}>
                {getWelcomeMessage()}, {user?.username}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  size="small"
                  label={user?.vehicle_number || "No Vehicle"}
                  variant="outlined"
                  color="primary"
                />
                {user?.is_admin && (
                  <Chip
                    size="small"
                    label="Admin"
                    color="secondary"
                    variant="outlined"
                  />
                )}
              </Box>
            </Box>
          </Box>

          {/* Quick Stats */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" color="primary">
                {dashboardData.historicalChecklists.length}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Checklists
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" color="success.main">
                {calculateCompletionScore()}%
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Complete
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" color="warning.main">
                {dashboardData.upcomingMaintenance.length}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Due Soon
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Safety Letter Alert */}
      {safetyLetter && !safetyLetter.has_signed && (
        <Card className="mobile-card" sx={{ margin: '8px 16px', bgcolor: 'warning.light' }}>
          <CardContent sx={{ pb: '16px !important' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <NotificationIcon color="warning" sx={{ mr: 2 }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  Safety Letter Required
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Please sign the monthly safety letter
                </Typography>
              </Box>
              <Button
                size="small"
                variant="contained"
                color="warning"
                sx={{ minHeight: 36 }}
                onClick={() => setSafetyLetterDialogOpen(true)}
                className="touch-target"
              >
                Sign
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions */}
      <Card className="mobile-card" sx={{ margin: '8px 16px' }}>
        <CardContent sx={{ pb: '16px !important' }}>
          <Typography variant="h6" className="mobile-subtitle" gutterBottom>
            Quick Actions
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
            <Button
              component={RouterLink}
              to="/new-checklist"
              variant="outlined"
              startIcon={<ChecklistIcon />}
              className="touch-target"
              sx={{
                flexDirection: 'column',
                gap: 1,
                minHeight: 64,
                '& .MuiButton-startIcon': {
                  margin: 0
                }
              }}
            >
              <ChecklistIcon />
              <Typography variant="caption">New Checklist</Typography>
            </Button>
            <Button
              component={RouterLink}
              to="/expense-report"
              variant="outlined"
              startIcon={<ExpenseIcon />}
              className="touch-target"
              sx={{
                flexDirection: 'column',
                gap: 1,
                minHeight: 64,
                '& .MuiButton-startIcon': {
                  margin: 0
                }
              }}
            >
              <ExpenseIcon />
              <Typography variant="caption">Expenses</Typography>
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Expense Report Section */}
      <MobileExpenseCard 
        report={dashboardData.currentExpenseReport} 
        bankStatement={dashboardData.bankStatement}
      />

      {/* Service Status Cards */}
      <MobileServiceCard 
        title="Oil Change"
        lastServiceDate={dashboardData.latestChecklist?.oil_change_next_date}
        historicalData={dashboardData.historicalChecklists}
        mileageInterval={5000}
        yearInterval={0.417}
        description="Due every 5,000 miles or 5 months"
      />

      <MobileServiceCard 
        title="Engine Service"
        lastServiceDate={dashboardData.latestChecklist?.engine_tuneup_date}
        historicalData={dashboardData.historicalChecklists}
        mileageInterval={12000}
        yearInterval={1}
        description="Due every 12,000 miles or annually"
      />

      <MobileServiceCard 
        title="Fire Extinguisher"
        lastServiceDate={dashboardData.latestChecklist?.fire_extinguisher_cert_date}
        yearInterval={1}
        description="Annual certification required"
      />

      {/* Recent Checklists */}
      <Card className="mobile-card" sx={{ margin: '8px 16px' }}>
        <CardContent sx={{ pb: '16px !important' }}>
          <Typography variant="h6" className="mobile-subtitle" gutterBottom>
            Recent Inspections
          </Typography>
          {dashboardData.historicalChecklists.length > 0 ? (
            <Box>
              {dashboardData.historicalChecklists.slice(0, 3).map((checklist, index) => (
                <Box key={checklist.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', py: 1 }}>
                    <Avatar
                      sx={{
                        bgcolor: 'success.light',
                        width: 32,
                        height: 32,
                        mr: 2
                      }}
                    >
                      <CheckIcon fontSize="small" />
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight="medium">
                        {new Date(checklist.date).toLocaleDateString()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Vehicle {checklist.vehicle_number} • {checklist.current_odometer?.toLocaleString()} miles
                      </Typography>
                    </Box>
                    <Button
                      component={RouterLink}
                      to={`/checklist/${checklist.id}`}
                      size="small"
                      className="touch-target"
                    >
                      View
                    </Button>
                  </Box>
                  {index < dashboardData.historicalChecklists.slice(0, 3).length - 1 && (
                    <Divider />
                  )}
                </Box>
              ))}
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <ChecklistIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                No recent inspections found
              </Typography>
              <Button
                component={RouterLink}
                to="/new-checklist"
                variant="outlined"
                size="small"
                sx={{ mt: 2 }}
              >
                Create First Checklist
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Bottom spacing for navigation */}
      <Box sx={{ height: 32 }} />

      {/* Safety Letter Signing Dialog */}
      <Dialog
        open={safetyLetterDialogOpen}
        onClose={() => !safetyLetterSigning && setSafetyLetterDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            m: 2
          }
        }}
      >
        <DialogTitle>
          Safety Letter Signature Required
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            Please review and sign the monthly safety letter for {safetyLetter && formatSafetyLetterDate(safetyLetter.year, safetyLetter.month)}.
          </Typography>

          <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5' }}>
            <Typography variant="subtitle2" gutterBottom>
              Title: {safetyLetter?.title || 'Monthly Safety Letter'}
            </Typography>
            <Button
              variant="contained"
              onClick={handleSafetyLetterView}
              startIcon={<DownloadIcon />}
              fullWidth
              sx={{ mt: 1 }}
              className="touch-target"
            >
              View Safety Letter
            </Button>
          </Paper>

          <TextField
            fullWidth
            label="Electronic Signature"
            value={safetyLetterSignature}
            onChange={(e) => setSafetyLetterSignature(e.target.value)}
            error={!!safetyLetterError}
            helperText={safetyLetterError || "Type your full name as your electronic signature"}
            disabled={safetyLetterSigning}
            sx={{ mt: 2 }}
            className="mobile-input"
          />

          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            By signing, you confirm that you have read and understood the safety letter.
            Your signature will be recorded with a timestamp and IP address for compliance purposes.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => setSafetyLetterDialogOpen(false)} 
            disabled={safetyLetterSigning}
            className="touch-target"
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSafetyLetterSign} 
            variant="contained"
            disabled={safetyLetterSigning || !safetyLetterSignature.trim() || !safetyLetterViewAttempted}
            startIcon={safetyLetterSigning ? <CircularProgress size={20} /> : null}
            className="touch-target"
          >
            {safetyLetterSigning ? 'Signing...' : 'Sign & Submit'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MobileDashboard; 